import {useAuthStore} from 'src/stores/auth.store'
import {env} from "@/env"

const RECONNECT_DELAY_MS = 1500

function getSocketProtocol() {
    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
        return 'wss'
    }
    return 'ws'
}

function getSocketUrl(token) {
    return `${getSocketProtocol()}://${env.socketIoServer}/cable?token=${token}`
}

function createSocketState() {
    return {
        raw: null,
        reconnectTimer: null,
        shouldReconnect: true,
        queue: [],
        userId: null,
        handlers: {
            onopen: null,
            onmessage: null,
            onclose: null,
            onerror: null
        },
        facade: null
    }
}

const socketIo = {
    io: createSocketState(),
    authIo: Object.assign(createSocketState(), {
        /** Multiple subscribers (e.g. editor AI + downloads) without overwriting `onmessage`. */
        authMessageListeners: new Set<(event: MessageEvent) => void | Promise<void>>()
    }),
    channels: []
}

function applyHandlers(state) {
    if (!state.raw) return
    const authListeners = (state as typeof socketIo.authIo).authMessageListeners
    if (authListeners instanceof Set) {
        state.raw.onmessage = (event: MessageEvent) => {
            for (const fn of authListeners) {
                try {
                    const result = fn(event)
                    if (result && typeof (result as Promise<void>).then === 'function') {
                        void (result as Promise<void>).catch(() => {})
                    }
                } catch {
                    /* ignore subscriber errors */
                }
            }
        }
    } else {
        state.raw.onmessage = state.handlers.onmessage
    }
    state.raw.onerror = event => {
        state.handlers.onerror?.(event)
    }
    state.raw.onclose = event => {
        if (state === socketIo.authIo) window.dispatchEvent(new CustomEvent('ai-assistant-connection', { detail: { connected: false } }))
        state.handlers.onclose?.(event)
        state.raw = null
        if (!state.shouldReconnect) return
        if (state.reconnectTimer) return
        state.reconnectTimer = window.setTimeout(() => {
            state.reconnectTimer = null
            connectSocket(state)
        }, RECONNECT_DELAY_MS)
    }
}

function flushQueue(state) {
    if (!state.raw || state.raw.readyState !== WebSocket.OPEN) return
    while (state.queue.length) {
        state.raw.send(state.queue.shift())
    }
}

function sendOrQueue(state, payload) {
    const message = typeof payload === 'string' ? payload : JSON.stringify(payload)
    if (state.raw?.readyState === WebSocket.OPEN) {
        state.raw.send(message)
        return
    }
    if (!state.queue.includes(message)) {
        state.queue.push(message)
    }
    if (!state.raw || state.raw.readyState === WebSocket.CLOSED) {
        connectSocket(state)
    }
}

function subscribeSchemaChannels() {
    socketIo.channels.forEach(channelId => {
        sendOrQueue(socketIo.io, {
            command: "subscribe",
            identifier: `{"channel":"SchemaChannel", "id":"${channelId}"}`
        })
    })
}

function subscribeUserChannel() {
    if (!socketIo.authIo.userId) return
    sendOrQueue(socketIo.authIo, {
        command: "subscribe",
        identifier: `{"channel":"UserChannel", "id":"${socketIo.authIo.userId}"}`
    })
}

function connectSocket(state) {
    const auth = useAuthStore()
    if (!auth.authToken) return state.facade
    if (state.raw && [WebSocket.OPEN, WebSocket.CONNECTING].includes(state.raw.readyState)) {
        return state.facade
    }

    if (state.reconnectTimer) {
        clearTimeout(state.reconnectTimer)
        state.reconnectTimer = null
    }

    state.shouldReconnect = true
    state.raw = new WebSocket(getSocketUrl(auth.authToken))

    state.raw.onopen = event => {
        if (state === socketIo.io) {
            subscribeSchemaChannels()
        } else {
            subscribeUserChannel()
        }
        flushQueue(state)
        if (state === socketIo.authIo) window.dispatchEvent(new CustomEvent('ai-assistant-connection', { detail: { connected: true } }))
        state.handlers.onopen?.(event)
    }

    applyHandlers(state)
    return state.facade
}

function createFacade(state) {
    if (state.facade) return state.facade

    const facade = {
        send(payload) {
            sendOrQueue(state, payload)
        },
        close(code, reason) {
            state.shouldReconnect = false
            if (state.reconnectTimer) {
                clearTimeout(state.reconnectTimer)
                state.reconnectTimer = null
            }
            if (state.raw) {
                state.raw.onopen = null
                state.raw.onmessage = null
                state.raw.onclose = null
                state.raw.onerror = null
                state.raw.close(code, reason)
            }
            state.raw = null
        }
    }

    Object.defineProperties(facade, {
        onopen: {
            get: () => state.handlers.onopen,
            set: value => {
                state.handlers.onopen = value
                if (state.raw) {
                    state.raw.onopen = event => {
                        if (state === socketIo.io) {
                            subscribeSchemaChannels()
                        } else {
                            subscribeUserChannel()
                        }
                        flushQueue(state)
                        if (state === socketIo.authIo) window.dispatchEvent(new CustomEvent('ai-assistant-connection', { detail: { connected: true } }))
                        state.handlers.onopen?.(event)
                    }
                }
            }
        },
        onmessage: {
            get: () => state.handlers.onmessage,
            set: value => {
                state.handlers.onmessage = value
                const authListeners = (state as typeof socketIo.authIo).authMessageListeners
                if (state.raw && !(authListeners instanceof Set)) {
                    state.raw.onmessage = value
                }
            }
        },
        onclose: {
            get: () => state.handlers.onclose,
            set: value => {
                state.handlers.onclose = value
                applyHandlers(state)
            }
        },
        onerror: {
            get: () => state.handlers.onerror,
            set: value => {
                state.handlers.onerror = value
                applyHandlers(state)
            }
        },
        readyState: {
            get: () => state.raw?.readyState ?? WebSocket.CLOSED
        },
        url: {
            get: () => state.raw?.url ?? getSocketUrl(useAuthStore().authToken)
        }
    })

    state.facade = facade
    return facade
}

function ensureSchemaSubscription(selectedTab) {
    if (!selectedTab) return
    if (!socketIo.channels.includes(selectedTab)) {
        socketIo.channels.push(selectedTab)
    }
    connectSocket(socketIo.io)
    subscribeSchemaChannels()
}

function ensureUserSubscription() {
    const auth = useAuthStore()
    if (!auth.user?.id) return
    socketIo.authIo.userId = auth.user.id
    connectSocket(socketIo.authIo)
    subscribeUserChannel()
}

export function unsubscribeSchemaChannel(selectedTab) {
    socketIo.channels = socketIo.channels.filter(channelId => channelId !== selectedTab)
    sendOrQueue(socketIo.io, {
        command: "unsubscribe",
        identifier: `{"channel":"SchemaChannel", "id":"${selectedTab}"}`
    })
}

export const useSocketIo = function (selectedTab) {
    ensureSchemaSubscription(selectedTab)
    return {
        io: createFacade(socketIo.io),
        authIo: createFacade(socketIo.authIo),
        channels: socketIo.channels
    }
}

export const useSocketIoAuth = function () {
    ensureUserSubscription()
    return createFacade(socketIo.authIo)
}

/** Subscribe to user (auth) WebSocket messages; multiple listeners stack. Returns unsubscribe. */
export function subscribeAuthIoMessage(handler: (event: MessageEvent) => void | Promise<void>) {
    const state = socketIo.authIo
    ensureUserSubscription()
    state.authMessageListeners.add(handler)
    applyHandlers(state)
    return () => {
        state.authMessageListeners.delete(handler)
        applyHandlers(state)
    }
}

export function disconnectSockets() {
    for (const state of [socketIo.io, socketIo.authIo]) {
        createFacade(state).close()
        state.queue = []
        state.userId = null
    }
    socketIo.channels = []
}
